import getApiUrl from '../configuration/getApiUrl';
import getCookie from '../functions/getCookie';
import type { TranscribeOptions } from '../stores/uploadSessionStore';
import {
  BYOK_FILE_SIZE_LIMIT,
  buildChunkBoundaries,
  buildChunkFromBuffer,
  prepareFileForTranscription,
} from './transcriptionAudio';

type TranscriptionCapabilities = {
  groq: {
    publicUrlModeAvailable: boolean;
  };
};

type TranscriptionProgressUpdate = {
  detail?: string;
  progress?: number;
  partialText?: string;
  authoritativeProgress?: boolean;
};

type Segment = {
  id?: number | string;
  start?: number;
  end?: number;
  text?: string;
  [key: string]: unknown;
};

type TranscriptionApiResponse = {
  text?: string;
  duration?: number;
  segments?: Segment[];
  error?: string;
  [key: string]: unknown;
};

type BackendJobCreateResponse = {
  job_id: string;
  task_id: string;
  status: string;
};

type BackendJobStatusResponse = {
  id: string;
  task_id?: string | null;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress?: number;
  detail?: string;
  partial_text?: string;
  result_text?: string;
  error?: string;
  audio_duration_sec?: number;
  note_id?: string | null;
};

type RequestOptions = {
  file?: File;
  options: TranscribeOptions;
  extraFields?: Array<[string, string]>;
};

type TranscriptionStrategy = 'direct' | 'url' | 'chunked' | 'backend_batch';

export type TranscriptionRunResult = {
  text: string;
  duration?: number;
  optimized: boolean;
  strategy: TranscriptionStrategy;
  chunkCount?: number;
  savedNoteId?: string | null;
};

function stripFileExtension(fileName: string) {
  return fileName.replace(/\.[^.]+$/, '');
}

function sleep(ms: number) {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

async function createBackendGroqJob(
  browserFile: File,
  options: TranscribeOptions,
): Promise<BackendJobCreateResponse> {
  const formData = new FormData();
  formData.append('file', browserFile);
  formData.append('provider', 'groq');
  formData.append('model', options.cloudModel || 'whisper-large-v3-turbo');
  formData.append('api_key', options.activeApiKey);
  formData.append('language', options.language || 'auto');
  formData.append('note_title', options.noteTitle || stripFileExtension(browserFile.name));
  if (options.noteFolder) {
    formData.append('note_folder', options.noteFolder);
  }

  const csrf = getCookie('csrftoken');
  const response = await fetch(`${getApiUrl()}/api/transcription/jobs/`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRFToken': csrf || '' },
    body: formData,
  });

  const data = (await response.json()) as BackendJobCreateResponse & { error?: string };
  if (!response.ok) {
    throw new Error(data.error || 'Failed to create backend transcription job.');
  }

  return data;
}

async function loadBackendGroqJob(jobId: string): Promise<BackendJobStatusResponse> {
  const response = await fetch(`${getApiUrl()}/api/transcription/jobs/${jobId}/`, {
    credentials: 'include',
  });
  const data = (await response.json()) as BackendJobStatusResponse & { error?: string };
  if (!response.ok) {
    throw new Error(data.error || 'Failed to load backend transcription job.');
  }
  return data;
}

async function waitForBackendGroqJob(
  jobId: string,
  onProgress?: (update: TranscriptionProgressUpdate) => void,
): Promise<TranscriptionRunResult> {
  for (;;) {
    const job = await loadBackendGroqJob(jobId);
    onProgress?.({
      detail: job.detail,
      progress: typeof job.progress === 'number' ? job.progress : undefined,
      partialText: job.partial_text || undefined,
      authoritativeProgress: true,
    });

    if (job.status === 'completed') {
      const text = (job.result_text || job.partial_text || '').trim();
      if (!text) {
        throw new Error('No text transcribed');
      }

      return {
        text,
        duration: job.audio_duration_sec,
        optimized: true,
        strategy: 'backend_batch',
        savedNoteId: job.note_id || null,
      };
    }

    if (job.status === 'failed') {
      throw new Error(job.error || 'Backend transcription failed.');
    }

    if (job.status === 'cancelled') {
      throw new Error('Backend transcription was cancelled.');
    }

    await sleep(1500);
  }
}

const DEFAULT_CAPABILITIES: TranscriptionCapabilities = {
  groq: {
    publicUrlModeAvailable: false,
  },
};

let capabilitiesPromise: Promise<TranscriptionCapabilities> | null = null;

async function loadTranscriptionCapabilities() {
  if (!capabilitiesPromise) {
    capabilitiesPromise = (async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/download/transcribe-capabilities/`, {
          credentials: 'include',
        });

        if (!response.ok) {
          return DEFAULT_CAPABILITIES;
        }

        const data = (await response.json()) as Partial<TranscriptionCapabilities>;
        return {
          groq: {
            publicUrlModeAvailable: Boolean(data?.groq?.publicUrlModeAvailable),
          },
        };
      } catch {
        return DEFAULT_CAPABILITIES;
      }
    })();
  }

  return capabilitiesPromise;
}

function normalizeWhitespace(value: string) {
  return value.replace(/\s+/g, ' ').trim();
}

function mergeTextWithOverlap(existingText: string, nextText: string) {
  const existing = normalizeWhitespace(existingText);
  const incoming = normalizeWhitespace(nextText);

  if (!existing) return incoming;
  if (!incoming) return existing;

  const existingWords = existing.split(' ');
  const incomingWords = incoming.split(' ');
  const maxOverlap = Math.min(60, existingWords.length, incomingWords.length);

  for (let overlap = maxOverlap; overlap > 0; overlap -= 1) {
    const existingTail = existingWords.slice(-overlap).join(' ');
    const incomingHead = incomingWords.slice(0, overlap).join(' ');
    if (existingTail === incomingHead) {
      return `${existing} ${incomingWords.slice(overlap).join(' ')}`.trim();
    }
  }

  return `${existing} ${incoming}`.trim();
}

async function requestTranscription({
  file,
  options,
  extraFields = [],
}: RequestOptions): Promise<TranscriptionApiResponse> {
  const formData = new FormData();

  if (file) {
    formData.append('file', file);
  }

  if (options.language !== 'auto') {
    formData.append('language', options.language);
  }

  if (options.mode === 'local') {
    formData.append('provider', 'local');
    formData.append('engine', options.localProvider);
    formData.append('model', options.activeLocalModel);
    formData.append('response_format', 'verbose_json');
  } else {
    formData.append('provider', options.cloudProvider);
    if (options.activeApiKey) {
      formData.append('api_key', options.activeApiKey);
    }
    formData.append('model', options.cloudModel || 'whisper-1');
    if (options.cloudProvider === 'openwhispr') {
      formData.append('openwhispr_cloud', 'true');
    }
    if (options.cloudProvider === 'custom' && options.normalizedCustomUrl) {
      formData.append('base_url', options.normalizedCustomUrl);
    }
    formData.append('response_format', 'verbose_json');
  }

  extraFields.forEach(([key, value]) => {
    formData.append(key, value);
  });

  const csrf = getCookie('csrftoken');
  const response = await fetch(`${getApiUrl()}/api/download/transcribe/`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRFToken': csrf || '' },
    body: formData,
  });

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? ((await response.json()) as TranscriptionApiResponse) : null;

  if (!response.ok) {
    if (response.status === 413) {
      throw new Error(
        'This file is larger than the current server upload limit. Please refresh and retry.',
      );
    }

    if (!isJson) {
      throw new Error(`Upload failed before transcription started (HTTP ${response.status}).`);
    }

    throw new Error(data?.error || `Error ${response.status}`);
  }

  return data ?? {};
}

function mergeChunkResponses(
  responses: Array<{
    data: TranscriptionApiResponse;
    startSec: number;
    endSec: number;
  }>,
) {
  const mergedSegments: Segment[] = [];
  let mergedText = '';
  let lastSegmentEnd = -1;
  let maxDuration = 0;

  responses.forEach(({ data, startSec, endSec }) => {
    maxDuration = Math.max(maxDuration, endSec);

    const segments = Array.isArray(data.segments) ? data.segments : [];
    if (segments.length > 0) {
      let chunkText = '';

      segments.forEach(segment => {
        const adjustedStart =
          typeof segment.start === 'number' ? segment.start + startSec : undefined;
        const adjustedEnd =
          typeof segment.end === 'number' ? segment.end + startSec : undefined;
        const segmentText = typeof segment.text === 'string' ? segment.text.trim() : '';

        if (!segmentText) {
          return;
        }

        if (typeof adjustedEnd === 'number' && adjustedEnd <= lastSegmentEnd + 0.35) {
          return;
        }

        mergedSegments.push({
          ...segment,
          start: adjustedStart,
          end: adjustedEnd,
        });

        if (typeof adjustedEnd === 'number') {
          lastSegmentEnd = Math.max(lastSegmentEnd, adjustedEnd);
        }

        chunkText = mergeTextWithOverlap(chunkText, segmentText);
      });

      mergedText = mergeTextWithOverlap(mergedText, chunkText);
      return;
    }

    if (typeof data.text === 'string' && data.text.trim()) {
      mergedText = mergeTextWithOverlap(mergedText, data.text);
    }
  });

  if (!mergedText && mergedSegments.length > 0) {
    mergedText = normalizeWhitespace(mergedSegments.map(segment => segment.text || '').join(' '));
  }

  return {
    text: mergedText.trim(),
    duration: maxDuration || undefined,
  };
}

export async function transcribeFileWithStrategy(
  browserFile: File,
  options: TranscribeOptions,
  onProgress?: (update: TranscriptionProgressUpdate) => void,
): Promise<TranscriptionRunResult> {
  const useBackendBatchForGroq =
    options.mode === 'cloud' &&
    options.cloudProvider === 'groq' &&
    (options.groqProcessingMode === 'batch' ||
      (options.groqProcessingMode === 'auto' && browserFile.size > BYOK_FILE_SIZE_LIMIT));

  if (useBackendBatchForGroq) {
    const batchReason =
      options.groqProcessingMode === 'batch'
        ? 'Groq batch mode selected. Uploading the file once and handing chunking to the backend job queue.'
        : 'Large Groq file detected. Uploading it once and handing chunking to the backend job queue.';

    const queuedReason =
      options.groqProcessingMode === 'batch'
        ? 'Backend Groq batch job queued. Waiting for worker progress.'
        : 'Backend Groq job queued. Waiting for worker progress.';

    onProgress?.({
      detail: batchReason,
      progress: 4,
      authoritativeProgress: true,
    });
    const job = await createBackendGroqJob(browserFile, options);
    onProgress?.({
      detail: `${queuedReason} (${job.job_id.slice(0, 8)})`,
      progress: 6,
      authoritativeProgress: true,
    });
    return waitForBackendGroqJob(job.job_id, onProgress);
  }

  if (
    options.mode === 'cloud' &&
    options.cloudProvider === 'groq' &&
    browserFile.size > BYOK_FILE_SIZE_LIMIT
  ) {
    onProgress?.({
      detail: 'Direct Groq mode selected. Optimizing locally and keeping chunking in the browser path.',
      progress: 4,
      authoritativeProgress: true,
    });
  }

  const prepared = await prepareFileForTranscription(
    browserFile,
    options.mode,
    options.cloudProvider,
  );

  if (options.mode !== 'cloud' || options.cloudProvider !== 'groq' || !prepared.stillOverLimit) {
    onProgress?.({
      detail: prepared.optimized
        ? 'Audio optimized to 16 kHz mono for transcription.'
        : 'Uploading audio for transcription.',
      progress: prepared.optimized ? 18 : 10,
    });

    const data = await requestTranscription({
      file: prepared.file,
      options,
    });
    const text = (data.text || '').trim();
    if (!text) {
      throw new Error('No text transcribed');
    }

    return {
      text,
      duration: data.duration,
      optimized: prepared.optimized,
      strategy: 'direct',
    };
  }

  const capabilities = await loadTranscriptionCapabilities();
  if (capabilities.groq.publicUrlModeAvailable) {
    onProgress?.({
      detail: 'Groq public URL mode is available. Handing the optimized file to the backend staging flow.',
      progress: 15,
    });

    const data = await requestTranscription({
      file: prepared.file,
      options,
    });
    const text = (data.text || '').trim();
    if (!text) {
      throw new Error('No text transcribed');
    }

    return {
      text,
      duration: data.duration,
      optimized: true,
      strategy: 'url',
    };
  }

  onProgress?.({
    detail: 'No public URL staging is configured. Splitting the file into Groq chunks.',
    progress: 8,
  });

  // When Groq chunking is required, prepareFileForTranscription can hand back
  // the decoded mono buffer so we do not pay the decode cost twice.
  if (!prepared.renderedBuffer) {
    throw new Error('Chunked Groq transcription requires a decoded audio buffer.');
  }

  const totalDurationSec = prepared.renderedBuffer.duration;
  const chunkBoundaries = buildChunkBoundaries(totalDurationSec);
  const responses: Array<{ data: TranscriptionApiResponse; startSec: number; endSec: number }> = [];

  for (const boundary of chunkBoundaries) {
    const chunk = buildChunkFromBuffer(
      prepared.renderedBuffer,
      browserFile.name,
      browserFile.lastModified,
      boundary,
    );
    onProgress?.({
      detail: `Transcribing Groq chunk ${chunk.index} of ${chunk.total}.`,
      progress: 10 + (chunk.index - 1) / chunk.total * 80,
      authoritativeProgress: true,
    });

    const data = await requestTranscription({
      file: chunk.file,
      options,
      extraFields: [['timestamp_granularities[]', 'segment']],
    });
    responses.push({
      data,
      startSec: chunk.startSec,
      endSec: chunk.endSec,
    });

    const preview = mergeChunkResponses(responses);
    onProgress?.({
      detail: `Received Groq chunk ${chunk.index} of ${chunk.total}.`,
      progress: 10 + chunk.index / chunk.total * 80,
      partialText: preview.text,
      authoritativeProgress: true,
    });
  }

  const merged = mergeChunkResponses(responses);
  if (!merged.text) {
    throw new Error('No text transcribed');
  }

  return {
    text: merged.text,
    duration: merged.duration ?? totalDurationSec,
    optimized: true,
    strategy: 'chunked',
    chunkCount: chunkBoundaries.length,
  };
}
